import { Tag } from '@carbon/react';
import { EntityLink } from 'components/entity-link';
import { EMPTY_CELL } from 'utils/empty-cell';
import { entityRefLabel } from 'utils/entity-id';
import type { AutoMatchDecisionRow } from './types';
import shared from '../admin-log.module.scss';
import styles from './decision-log.module.scss';

const NOT_RE_RESOLVED = 'not yet re-resolved';

/**
 * Who undid the decision, as "by <name>".
 * @param {string | null | undefined} undoneBy - The undoer's name
 * @return {string} The attribution
 */
const actor = (undoneBy: string | null | undefined) =>
  undoneBy ? `by ${undoneBy}` : 'by unknown';

interface ReversalCellProps {
  row: AutoMatchDecisionRow;
}

/**
 * The decision log's Reversal cell: what the gate matched, that an examiner took
 * it back, and what they resolved the party to instead.
 *
 * Everything sits on one line: the table's row height is fixed, so a wrapped
 * second line was cropped top and bottom. The detail truncates instead, the full
 * text rides on the title, and the column can be widened to read more.
 * @param {ReversalCellProps} props - The decision row
 * @return {JSX.Element} The undo summary, or the em dash for a standing decision
 */
export function ReversalCell({ row }: ReversalCellProps) {
  if (!row.undone) return <span className={shared.muted}>{EMPTY_CELL}</span>;

  const redone = row.redoneToId
    ? entityRefLabel(row.redoneToName, row.redoneToId)
    : NOT_RE_RESOLVED;

  return (
    <div
      className={styles.reversalCell}
      title={`Undone ${actor(row.undoneBy)} → ${redone}`}
    >
      {/* Holds its natural width: as a flex item it would otherwise shrink and
          clip its own label. */}
      <Tag type="red" size="sm" className={styles.reversalTag}>
        Undone
      </Tag>
      <span className={styles.reversalDetail}>
        {actor(row.undoneBy)}
        {' → '}
        <EntityLink
          id={row.redoneToId}
          name={row.redoneToName}
          fallback={NOT_RE_RESOLVED}
        />
      </span>
    </div>
  );
}
